import React, { useEffect } from "react";
import styles from './index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getLedgerById } from "../../../../redux/features/ledgers/ledgersService";
import TableSkeleton from "../../../../components/Skeleton";
import NoResultsFound from "../../../../components/NoResultsFound";
import { getCustomerById } from "../../../../redux/features/customers/customersService";
import Skeleton from "react-loading-skeleton";

const formattedAmount = (amount) =>
    new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);

const CustomerLedger = () => {

    const dispatch = useDispatch();
    const { id } = useParams();

    const { isLedgerLoading, ledger } = useSelector((state) => state.ledger.ledgerDetails);
    const {  customer } = useSelector((state) => state.customer.customer);

    useEffect(() => {
        dispatch(getLedgerById({ id }));
        dispatch(getCustomerById({ id }));
    }, [dispatch, id]);

    return (
        <div className={`card-section`}>
            <div className={`search-card`}>
                <div className={`search-card-header ${styles.ledgerHeader}`}>
                    {isLedgerLoading ?
                        <Skeleton width={200} />
                        :
                        <>
                            <h1 className={`form-title ${styles.ledgerTitle}`}>#{customer?.customer_code}</h1>
                            <p className={styles.ledgerText}>Ledger</p>
                        </>
                    }
                </div>
                <div className={`search-card-body ${styles.ledgerBody}`}>
                    {isLedgerLoading ?
                        <TableSkeleton columns={["Description", "Created Date", "Total Selling Price", "Currency", "EXR", "Deposit", "Remaining Balance"]} rowCount={10} height={20} />
                        : ledger?.orders?.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr className={styles.ledgerHRow}>
                                            <th className={styles.ledgerH}>Date</th>
                                            <th className={styles.ledgerH}>Description</th>
                                            <th className={styles.ledgerH}>Selling Price</th>
                                            <th className={styles.ledgerH}>Total Selling Price</th>
                                            <th className={styles.ledgerH}>Currency</th>
                                            <th className={styles.ledgerH}>EXR</th>
                                            <th className={styles.ledgerH}>Deposit</th>
                                            <th className={styles.ledgerH}>Remaining Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody className="tbody">
                                        {(() => {
                                            let cumulativeTotal = 0;
                                            let cumulativePaid = 0;

                                            return ledger?.orders
                                                ?.slice() // make a shallow copy
                                                .sort((a, b) => new Date(a.order.created_at) - new Date(b.order.created_at)).flatMap((item, index) => {
                                                    const rows = [];

                                                    cumulativeTotal += item.order_total;
                                                    const orderRemainingBalance = cumulativeTotal - cumulativePaid;

                                                    rows.push(
                                                        <tr key={`order-${item.order.id}`} className={styles.orderRow}>
                                                            <td className={styles.ledgerData} style={{ textAlign: 'center' }}>
                                                                {new Date(item.order.created_at).toLocaleDateString()}
                                                            </td>
                                                            <td className={styles.ledgerData} style={{ textAlign: 'center' }}>
                                                                Order {item.order.id}
                                                            </td>
                                                            <td></td>
                                                            <td className={styles.ledgerData} style={{ textAlign: 'right' }}>
                                                                {formattedAmount(item.order_total)}
                                                            </td>
                                                            <td className={styles.ledgerData}></td>
                                                            <td className={styles.ledgerData}></td>
                                                            <td className={styles.ledgerData}></td>
                                                            <td className={styles.ledgerData} colSpan={2} style={{ textAlign: 'right' }}>
                                                                {formattedAmount(orderRemainingBalance)}
                                                            </td>
                                                        </tr>
                                                    );

                                                    // Step 2: VEHICLE ROWS
                                                    item.order.vehicles?.forEach((vehicle) => {
                                                        const vehiclePrice = vehicle.vehicle_costs?.[0]?.selling_price || 0;

                                                        rows.push(
                                                            <tr key={`vehicle-${vehicle.id}`} className={styles.vehicleRow}>
                                                                <td className={styles.ledgerData} style={{ textAlign: 'center' }}>
                                                                    {new Date(item.order.created_at).toLocaleDateString()}
                                                                </td>
                                                                <td className={styles.ledgerData} style={{ textAlign: 'center' }}>
                                                                    Stock No #{vehicle.stock_no}
                                                                </td>
                                                                <td className={styles.ledgerData} style={{ textAlign: 'right' }}>
                                                                    {vehiclePrice.toLocaleString()}
                                                                </td>
                                                                <td className={styles.ledgerData}></td>
                                                                <td className={styles.ledgerData}></td>
                                                                <td className={styles.ledgerData}></td>
                                                                <td className={styles.ledgerData}></td>
                                                                <td className={styles.ledgerData}></td>
                                                                {/* <td className={styles.ledgerData} colSpan={2} style={{ textAlign: 'right' }}>
                                                                {vehicleRemainingBalance.toLocaleString()}
                                                            </td> */}
                                                            </tr>
                                                        );
                                                    });

                                                    // Step 3: PAYMENT ROWS
                                                    const validPayments = item.payments.filter(
                                                        (p) => !['Pending', 'Payment Rejected'].includes(p.payment_status))
                                                        .sort((a, b) => a.id - b.id);

                                                    validPayments.forEach((payment, pIndex) => {
                                                        cumulativePaid += payment.amount;
                                                        const paymentRemainingBalance = cumulativeTotal - cumulativePaid;

                                                        rows.push(
                                                            <tr key={`payment-${payment.id}`} className={styles.paymentRow}>
                                                                <td className={styles.ledgerData} style={{ textAlign: 'center' }}>
                                                                    {new Date(payment.createdAt).toLocaleDateString()}
                                                                </td>
                                                                <td className={styles.ledgerData} style={{ textAlign: 'center' }}>
                                                                    Order {item.order.id} Payment {pIndex + 1}
                                                                </td>
                                                                <td className={styles.ledgerData}></td>
                                                                <td className={styles.ledgerData}></td>
                                                                <td className={styles.ledgerData} style={{ textAlign: 'center', fontWeight: 'bold' }}>{payment.currency}</td>
                                                                <td className={styles.ledgerData} style={{ textAlign: 'right' }}>{formattedAmount(payment.exchange_rate)}</td>
                                                                <td className={styles.ledgerData} style={{ textAlign: 'right' }}>
                                                                    {formattedAmount(payment.amount)}
                                                                </td>
                                                                <td className={styles.ledgerData} colSpan={2} style={{ textAlign: 'right' }}>
                                                                    {formattedAmount(paymentRemainingBalance)}
                                                                </td>
                                                            </tr>
                                                        );
                                                    });
                                                    return rows;
                                                });
                                        })()}
                                        <tr className={styles.totalRow}>
                                            <td className={styles.ledgerData} colSpan={3} style={{ textAlign: 'center' }}>
                                                Total
                                            </td>
                                            <td className={styles.ledgerData} style={{ textAlign: 'right' }}>{formattedAmount(ledger?.grand_total)}</td>
                                            <td className={styles.ledgerData}></td>
                                            <td className={styles.ledgerData}></td>
                                            <td className={styles.ledgerData} style={{ textAlign: 'right' }}>
                                                {formattedAmount(ledger?.total_paid)}
                                            </td>
                                            <td className={styles.ledgerData} colSpan={2} style={{ textAlign: 'right' }}>
                                                {formattedAmount(ledger?.total_balance)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
            </div>
        </div>
    );
};

export default CustomerLedger;
